"use client";

import Image from "next/image";
import Link from "next/link";
import { InquiryDialog } from "@/components/trip/InquiryDialog";
import { formatPrice } from "@/lib/format";
import { resolveMediaUrl } from "@/lib/media";
import { useBookingPrice } from "@/components/trip/BookingPrice";
import type { GroupPrice, Media } from "@/lib/dto";

type Props = {
  title: string;
  slug: string;
  price: number;
  currency: string;
  /** Struck-through "was" price — only shown when higher than the live price. */
  wasPrice?: number;
  /** Group-size price brackets for the selected tier (its `groupPrices`, not the whole `PackagePricing` envelope). */
  pricing?: GroupPrice[];
  /** Tenant contact phone for the WhatsApp row (optional). */
  phone?: string;
  ribbon?: string;
  duration: number;
  duration_unit: "days" | "hours" | string;
  /** Site-wide TripAdvisor social proof, shown under the CTAs. */
  tripadvisorUrl?: string;
  tripadvisorReviewCount?: number;
  /** Avatars for the "Instant assistance" card. */
  agentAvatars?: Media[];
  /** Optional selected tier price that overrides the default price. */
  selectedTierPrice?: number;
};

/**
 * Sticky sidebar for the package detail page — matches production's real
 * markup exactly (confirmed via the live DOM): two stacked cards.
 *
 * 1. Price card — "On Sale" ribbon, struck-through original price with a
 *    red "N OFF" pill, the live price + "per person" + a plain
 *    "Group Discounts Available!" line (production shows NO group-price
 *    table here — that was an earlier invention; the real bracket table
 *    lives in the Cost Details section), then "check availability" and
 *    "Make an Enquiry" CTAs and a TripAdvisor "rated excellent" widget.
 * 2. Contact card — overlapping agent avatars, "Instant assistance? Let's
 *    talk!", a WhatsApp link, and an experience footnote.
 */
export function BookingModule({
  title,
  slug,
  price,
  currency,
  wasPrice,
  phone,
  ribbon,
  pricing,
  tripadvisorUrl,
  tripadvisorReviewCount,
  agentAvatars,
  duration,
  duration_unit,
  selectedTierPrice: propPrice,
}: Props) {
  const { selectedTierPrice: contextPrice } = useBookingPrice();

  const displayPrice =
    contextPrice !== undefined
      ? contextPrice
      : propPrice !== undefined
        ? propPrice
        : price;
  const livePrice = formatPrice(displayPrice, currency);
  const showWas =
    typeof wasPrice === "number" && wasPrice > displayPrice ? wasPrice : null;
  const savings = showWas ? showWas - displayPrice : null;
  const waPhone = phone ? phone.replace(/[^0-9]/g, "") : "";
  //const avatars = (agentAvatars ?? []).slice(0, 2);
  const avatars = [
    { src: "/ram.webp", alt: "Ram Adhikari" },
    { src: "/sanjib.webp", alt: "Sanjib Adhikari" },
  ];

  return (
    <>
      <div className="relative z-10 rounded-md border border-line bg-white p-6 shadow-md before:absolute before:inset-0 before:bg-[url('/general-pattern.png')] before:pointer-none: before:-z-10 before:opacity-10">
        {ribbon ? (
          <span className="ribbon on-sales text-[0.75rem] py-0.5 font-extrabold font-secondary bg-warning uppercase">
            {ribbon}
          </span>
        ) : null}

        <div className="relative font-secondary leading-[1.2]">
          {showWas ? (
            <span className="block text-[0.9375rem] font-bold text-muted line-through">
              {formatPrice(showWas, currency)}
              {savings ? (
                <i className="ml-2 inline-block rounded-full bg-danger/10 p-[3px_10px] px-2.5 py-[3px] text-xxs font-bold text-danger not-italic">
                  {formatPrice(savings, currency)} OFF
                </i>
              ) : null}
            </span>
          ) : null}
          <div className="flex flex-col gap-x-1">
            <div className="flex w-full items-center gap-x-1">
              <span className="text-xl font-extrabold text-primary lg:text-[1.5rem]">
                {livePrice}
              </span>
              <span className="text-xs font-bold text-headings/80">
                per person
              </span>
            </div>
            <span className="block pt-0.5 text-xs font-semibold text-muted">
              {duration_unit === "days"
                ? `${duration - 1} Nights ${duration} Days Trip`
                : null}
            </span>
          </div>
        </div>

        {pricing && pricing.length > 1 ? (
          <div className="mt-4 mb-3 text-sm relative py-4 z-10 before:-z-10 before:absolute before:-inset-x-[22px] before:bg-primary/5 before:border-y-1 before:border-y-primary/20 before:inset-y-0">
            <h4 className="mb-0 text-hesdings text-md font-bold flex items-center gap-x-2">
              View Group Size &amp; Price
            </h4>
            <ul className="[&amp;&gt;li+li]:mt-1 text-text text-xxs pt-3">
              {pricing.map((item) => {
                return (
                  <li className="py-0.5 font-bold" key={item.id}>
                    <span>
                      {item.minPerson === item.maxPerson
                        ? `${item.minPerson} Pax`
                        : `${item.minPerson} - ${item.maxPerson} Pax`}
                    </span>
                    <span className="divider mx-2.5">-----</span>
                    <span>US$ {item.price}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <div className="mt-4">
          <a
            href="#departure-dates"
            className="block w-full rounded-lg border border-primary bg-primary px-6 py-2.5 text-center text-[0.9375rem] font-bold text-white capitalize"
          >
            check availability
          </a>
          <InquiryDialog
            title="Make an Enquiry"
            slug={slug}
            trigger={
              <button
                type="button"
                className="mt-1 w-full rounded-lg border border-headings bg-primary/5 hover:bg-primary hover:text-white px-6 py-1.5 text-[0.9375rem] font-bold text-headings capitalize hover:border-primary"
              >
                Make an Enquiry
              </button>
            }
          />
        </div>

        {tripadvisorUrl && tripadvisorReviewCount ? (
          <div className="mx-auto mt-3 max-w-[140px] text-center text-xxs text-headings/80 lg:max-w-[200px]">
            <a href={tripadvisorUrl} target="_blank" rel="nofollow noreferrer">
              {title ? null : null}
              Rated <b>&ldquo;excellent&rdquo;</b> by{" "}
              <b>{tripadvisorReviewCount.toLocaleString()} travelers</b>
              <span className="mt-1 block">
                <svg className="mx-auto h-[22px] w-[100px]" aria-hidden>
                  <use xlinkHref="/icons.svg#tripadvisorbase" />
                </svg>
              </span>
            </a>
          </div>
        ) : null}
      </div>

      {waPhone || avatars.length ? (
        <div className="mt-3 rounded-md border border-line bg-[#EFF0F1] p-6 shadow-md">
          {avatars.length ? (
            <ul className="flex items-center [&>li+li]:-ml-4">
              {avatars.map((avatar, i) => {
                return (
                  <li key={i}>
                    <span className="block h-[50px] w-[50px] overflow-hidden rounded-full border-[3px] border-white bg-headings">
                      {avatar.src ? (
                        <Image
                          src={avatar.src}
                          alt={avatar.alt}
                          width={60}
                          height={60}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}

          <p className="my-3 block font-secondary text-base font-bold">
            Instant assistance? Let&rsquo;s talk!
          </p>

          {waPhone ? (
            <Link
              href={`https://api.whatsapp.com/send?phone=${waPhone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative inline-block pr-9 text-right font-secondary leading-[1.35]"
            >
              <svg
                className="absolute top-0 right-0 mr-1 h-6 w-6 text-primary"
                aria-hidden
              >
                <use xlinkHref="/icons.svg#whatsapp" />
              </svg>
              <span className="block font-bold text-headings">{waPhone}</span>
            </Link>
          ) : null}

          <p className="mt-3 border-t leading-[1.4] border-t-headings/30 pt-3 text-sm font-medium text-muted">
            Travel pros with{" "}
            <b className="text-headings mx-1">13+ years of experience</b>{" "}
            let&rsquo;s plan your trip!
          </p>
        </div>
      ) : null}
    </>
  );
}
